/**
 * ProductStockLoader
 *
 * Loads stock availability for product cards in chunks, sequentially.
 * Desktop: 8 per chunk, Mobile: 4 per chunk.
 * Re-initializes when #product-list DOM changes (AJAX pagination).
 *
 * NOTE: This loader hits /stock/availabilityajax which is backed by a 60s Redis
 * cache (see Controller\Site\Stock::availabilityajaxAction). It serves as a
 * REAL-TIME GUARDRAIL on top of the Elasticsearch document, whose stock_status
 * field is refreshed every ~3 minutes by /v1/productindexers/cronstock.
 *
 * Do not raise the Redis TTL — if the AJAX cache becomes older than ES, the
 * card UI will show stale state and the guardrail loses its purpose.
 *
 * When `RUNTIME_CONFIG.global_website_enable_stock_check_on_listing` is not 1,
 * this module no-ops (no fetch, no MutationObserver) — matches the website
 * setting `website_enable_stock_check_on_listing`.
 */
(function () {
  "use strict";

  var CHUNK_SIZE_DESKTOP = 8;
  var CHUNK_SIZE_MOBILE = 4;
  var CARD_SELECTOR = ".product-card[data-product-id]";
  var BADGE_SELECTOR = ".product-stock-badge";
  var QUICK_ADD_SELECTOR = ".product-quick-add-to-cart-btn";

  var STATUS_LABELS = {
    in_stock: (window.__STOREFRONT_I18N__?.product?.stock?.in_stock) || "Còn hàng",
    low_stock: (window.__STOREFRONT_I18N__?.product?.stock?.low_stock) || "Sắp hết hàng",
    out_of_stock: (window.__STOREFRONT_I18N__?.product?.stock?.out_of_stock) || "Hết hàng",
  };

  var STATUS_CLASSES = {
    in_stock:
      "inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700",
    low_stock:
      "inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-orange-50 text-orange-600",
    out_of_stock:
      "inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-600",
  };

  var STATUS_BADGE_CLASSES = {
    in_stock: "product-stock-badge-instock",
    low_stock: "product-stock-badge-lowstock",
    out_of_stock: "product-stock-badge-outofstock",
  };

  var STATUS_CARD_CLASSES = {
    in_stock: "product-card-instock",
    low_stock: "product-card-lowstock",
    out_of_stock: "product-card-outofstock",
  };

  function ProductStockLoader() {
    this.mutationObserver = null;
    this.loadAll();
    this.watchDom();
  }

  ProductStockLoader.prototype.loadAll = function () {
    var allCards = document.querySelectorAll(CARD_SELECTOR);
    if (allCards.length === 0) return;

    var cards = [];
    for (var i = 0; i < allCards.length; i++) {
      cards.push(allCards[i]);
    }

    var chunkSize =
      window.innerWidth >= 768 ? CHUNK_SIZE_DESKTOP : CHUNK_SIZE_MOBILE;
    var chunks = [];
    for (var i = 0; i < cards.length; i += chunkSize) {
      chunks.push(cards.slice(i, i + chunkSize));
    }

    this.loadChunksSequentially(chunks, 0);
  };

  ProductStockLoader.prototype.loadChunksSequentially = function (chunks, index) {
    if (index >= chunks.length) return;

    var self = this;
    this.loadChunk(chunks[index], function () {
      self.loadChunksSequentially(chunks, index + 1);
    });
  };

  ProductStockLoader.prototype.loadChunk = function (cards, callback) {
    var uniqueIds = [];
    var seen = {};
    var cardMap = {};

    for (var i = 0; i < cards.length; i++) {
      var pid = parseInt(cards[i].getAttribute("data-product-id"), 10);
      if (pid > 0 && !seen[pid]) {
        uniqueIds.push(pid);
        seen[pid] = true;
        cardMap[pid] = [];
      }
      if (pid > 0) {
        cardMap[pid].push(cards[i]);
      }
    }

    if (uniqueIds.length === 0) {
      if (callback) callback();
      return;
    }

    var url =
      "/stock/availabilityajax?product_ids=" +
      uniqueIds.join(",") +
      "&desired_uom_quantity=1";

    var pageParams = new URLSearchParams(window.location.search);
    if (pageParams.has("live")) {
      url += "&live=" + (pageParams.get("live") || "1");
    }

    var self = this;
    fetch(url, {
      method: "GET",
      credentials: "same-origin",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        var items = (json.data && json.data.items) || [];
        self.applyResults(items, cardMap);
        if (callback) callback();
      })
      .catch(function () {
        if (callback) callback();
      });
  };

  ProductStockLoader.prototype.applyResults = function (items, cardMap) {
    var statusByProduct = {};
    for (var i = 0; i < items.length; i++) {
      var pid = items[i].product_id;
      var status = items[i].status || "out_of_stock";
      if (
        !statusByProduct[pid] ||
        this.statusPriority(status) >
          this.statusPriority(statusByProduct[pid])
      ) {
        statusByProduct[pid] = status;
      }
    }

    for (var productId in cardMap) {
      if (!cardMap.hasOwnProperty(productId)) continue;
      var cards = cardMap[productId];
      var productStatus = statusByProduct[productId] || "out_of_stock";

      for (var j = 0; j < cards.length; j++) {
        this.updateCard(cards[j], productStatus);
      }
    }
  };

  ProductStockLoader.prototype.statusPriority = function (status) {
    if (status === "in_stock") return 3;
    if (status === "low_stock") return 2;
    if (status === "insufficient_stock") return 1;
    return 0;
  };

  ProductStockLoader.prototype.updateCard = function (card, status) {
    var badge = card.querySelector(BADGE_SELECTOR);
    if (!badge) return;

    badge.setAttribute("data-stock-status", status);

    card.classList.remove(
      "product-card-instock",
      "product-card-lowstock",
      "product-card-outofstock"
    );
    card.classList.add(
      STATUS_CARD_CLASSES[status] || STATUS_CARD_CLASSES.out_of_stock
    );

    if (status === "out_of_stock") {
      badge.className =
        "product-stock-badge " +
        STATUS_BADGE_CLASSES.out_of_stock +
        " " +
        STATUS_CLASSES.out_of_stock;
      badge.textContent = STATUS_LABELS.out_of_stock;
      badge.classList.remove("hidden");

      var img = card.querySelector(".product-card-image");
      if (img) img.classList.add("opacity-60");

      var quickAddBtns = card.querySelectorAll(QUICK_ADD_SELECTOR);
      for (var i = 0; i < quickAddBtns.length; i++) {
        quickAddBtns[i].setAttribute("disabled", "disabled");
        quickAddBtns[i].classList.add("pointer-events-none", "opacity-50");
      }
    } else {
      badge.className = "product-stock-badge hidden";
    }
  };

  ProductStockLoader.prototype.watchDom = function () {
    var productList = document.getElementById("product-list");
    if (!productList || !("MutationObserver" in window)) return;

    var self = this;
    var timer = null;

    this.mutationObserver = new MutationObserver(function (mutations) {
      var hasProductCard = false;
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1 && added[j].querySelector) {
            if (
              added[j].classList.contains("product-card") ||
              added[j].querySelector(".product-card")
            ) {
              hasProductCard = true;
              break;
            }
          }
        }
        if (hasProductCard) break;
      }

      if (hasProductCard) {
        if (timer) clearTimeout(timer);
        timer = setTimeout(function () {
          self.loadAll();
        }, 150);
      }
    });

    this.mutationObserver.observe(productList, {
      childList: true,
      subtree: true,
    });
  };

  function isListingStockAjaxEnabled() {
    var cfg = window.RUNTIME_CONFIG || {};
    return parseInt(cfg.global_website_enable_stock_check_on_listing, 10) === 1;
  }

  if (!isListingStockAjaxEnabled()) {
    return;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      new ProductStockLoader();
    });
  } else {
    new ProductStockLoader();
  }
})();
